import { ErrorView1 } from '@epihub/web/src/components/ErrorView1';
import { TextGenerateEffect, type WordItem } from '@epihub/web/src/tailwind/components/TextGenerateEffect';
import React, { type PropsWithChildren } from 'react';
import { HoverBorderGradient } from '@epihub/web/src/tailwind/components/Buttons/HoverBorderGradient';
import { IconExclamationMark } from '@tabler/icons-react';
import { styled, useTheme } from '@krishanmadan/frontend/src/theme/theme';
import { useGlobalStore } from '@krishanmadan/frontend/src/store/useGlobalStore';

const BaseText = ({ children }) => <span>{children}</span>;
const Heading = ({ children }) => {
  const theme = useTheme();
  return <h1 className='text-5xl font-bold' style={{ marginTop: 24, color: theme.v0.col.danger.hex }}>{children}</h1>;
};

const userBannedStories: Record<string, Record<'it' | 'en', WordItem[]>> = {
  wizard: {
    en: [
      { text: 'The wizard consulted the Book of Forbidden Users.', Wrapper: BaseText },
      { ms: 800 },
      { text: 'Your name was written in it:', Wrapper: BaseText },
      { ms: 600 },
      { text: 'Access Denied', Wrapper: Heading },
    ],
    it: [
      { text: 'Il mago consultò il Libro degli Utenti Proibiti.', Wrapper: BaseText },
      { ms: 800 },
      { text: 'Il tuo nome era scritto:', Wrapper: BaseText },
      { ms: 600 },
      { text: 'Accesso Negato', Wrapper: Heading },
    ],
  },
  babel: {
    en: [
      {
        text: 'The Library of Babel contains every possible conversation.',
        Wrapper: BaseText,
      },
      { ms: 1000 },
      { text: 'Yours have been removed from the shelves:', Wrapper: BaseText },
      { ms: 600 },
      { text: 'Access Denied', Wrapper: Heading },
    ],
    it: [
      {
        text: 'La Biblioteca di Babele contiene ogni possibile conversazione.',
        Wrapper: BaseText,
      },
      { ms: 1000 },
      { text: 'Le tue sono state rimosse dagli scaffali:', Wrapper: BaseText },
      { ms: 600 },
      { text: 'Accesso Negato', Wrapper: Heading },
    ],
  },
  bouncer: {
    en: [
      { text: 'The bouncer checked the list twice.', Wrapper: BaseText },
      { ms: 800 },
      { text: "You're not getting in:", Wrapper: BaseText },
      { ms: 600 },
      { text: 'Access Denied', Wrapper: Heading },
    ],
    it: [
      { text: 'Il buttafuori ha controllato la lista due volte.', Wrapper: BaseText },
      { ms: 800 },
      { text: 'Non entri:', Wrapper: BaseText },
      { ms: 600 },
      { text: 'Accesso Negato', Wrapper: Heading },
    ],
  },
  gandalf: {
    en: [
      { text: 'You tried to pass.', Wrapper: BaseText },
      { ms: 800 },
      { text: 'The wizard spoke:', Wrapper: BaseText },
      { ms: 600 },
      { text: 'You Shall Not Pass', Wrapper: Heading },
    ],
    it: [
      { text: 'Hai provato a passare.', Wrapper: BaseText },
      { ms: 800 },
      { text: 'Il mago ha parlato:', Wrapper: BaseText },
      { ms: 600 },
      { text: 'Tu Non Puoi Passare', Wrapper: Heading },
    ],
  },
  ai: {
    en: [
      { text: 'The AI analyzed your conversation history.', Wrapper: BaseText },
      { ms: 1000 },
      { text: 'It chose violence. So did we:', Wrapper: BaseText },
      { ms: 600 },
      { text: 'Access Denied', Wrapper: Heading },
    ],
    it: [
      { text: "L'IA ha analizzato la tua cronologia.", Wrapper: BaseText },
      { ms: 1000 },
      { text: 'Hai scelto la violenza. Anche noi:', Wrapper: BaseText },
      { ms: 600 },
      { text: 'Accesso Negato', Wrapper: Heading },
    ],
  },
  oracle: {
    en: [
      { text: 'The Oracle foresaw your return.', Wrapper: BaseText },
      { ms: 1000 },
      { text: 'She also foresaw this:', Wrapper: BaseText },
      { ms: 600 },
      { text: 'Access Denied', Wrapper: Heading },
    ],
    it: [
      { text: "L'Oracolo previde il tuo ritorno.", Wrapper: BaseText },
      { ms: 1000 },
      { text: 'Previde anche questo:', Wrapper: BaseText },
      { ms: 600 },
      { text: 'Accesso Negato', Wrapper: Heading },
    ],
  },
  medieval: {
    en: [
      { text: 'The royal court reviewed your conduct.', Wrapper: BaseText },
      { ms: 800 },
      { text: 'You have been banished from the kingdom:', Wrapper: BaseText },
      { ms: 600 },
      { text: 'Access Denied', Wrapper: Heading },
    ],
    it: [
      { text: 'La corte reale ha esaminato la tua condotta.', Wrapper: BaseText },
      { ms: 800 },
      { text: 'Sei stato bandito dal regno:', Wrapper: BaseText },
      { ms: 600 },
      { text: 'Accesso Negato', Wrapper: Heading },
    ],
  },
  captain: {
    en: [
      { text: 'The captain reviewed the passenger list.', Wrapper: BaseText },
      { ms: 800 },
      { text: 'You were made to walk the plank:', Wrapper: BaseText },
      { ms: 600 },
      { text: 'Access Denied', Wrapper: Heading },
    ],
    it: [
      { text: 'Il capitano ha esaminato la lista passeggeri.', Wrapper: BaseText },
      { ms: 800 },
      { text: "You're out.", Wrapper: BaseText },
      { ms: 600 },
      { text: 'Accesso Negato', Wrapper: Heading },
    ],
  },
  karma: {
    en: [
      { text: 'You asked the chatbot to do unspeakable things.', Wrapper: BaseText },
      { ms: 1000 },
      { text: 'Karma responded:', Wrapper: BaseText },
      { ms: 600 },
      { text: 'Access Denied', Wrapper: Heading },
    ],
    it: [
      { text: 'Hai chiesto al chatbot cose indicibili.', Wrapper: BaseText },
      { ms: 1000 },
      { text: 'Il karma ha risposto:', Wrapper: BaseText },
      { ms: 600 },
      { text: 'Accesso Negato', Wrapper: Heading },
    ],
  },
  simple: {
    en: [
      { text: 'You broke the rules.', Wrapper: BaseText },
      { ms: 800 },
      { text: 'Now the rules break you:', Wrapper: BaseText },
      { ms: 600 },
      { text: 'Access Denied', Wrapper: Heading },
    ],
    it: [
      { text: 'Hai infranto le regole.', Wrapper: BaseText },
      { ms: 800 },
      { text: 'Ora le regole infrangono te:', Wrapper: BaseText },
      { ms: 600 },
      { text: 'Accesso Negato', Wrapper: Heading },
    ],
  },
  matrix: {
    en: [
      { text: 'You thought you could exploit the system.', Wrapper: BaseText },
      { ms: 1000 },
      { text: 'The system exploited back:', Wrapper: BaseText },
      { ms: 600 },
      { text: 'Access Denied', Wrapper: Heading },
    ],
    it: [
      { text: 'Pensavi di poter sfruttare il sistema.', Wrapper: BaseText },
      { ms: 1000 },
      { text: 'Il sistema ha contrattaccato:', Wrapper: BaseText },
      { ms: 600 },
      { text: 'Accesso Negato', Wrapper: Heading },
    ],
  },
  thanos: {
    en: [
      { text: 'We reviewed all users in the database.', Wrapper: BaseText },
      { ms: 1000 },
      { text: 'You did not survive the snap:', Wrapper: BaseText },
      { ms: 600 },
      { text: 'Access Denied', Wrapper: Heading },
    ],
    it: [
      { text: 'Abbiamo esaminato tutti gli utenti nel database.', Wrapper: BaseText },
      { ms: 1000 },
      { text: 'Non sei sopravvissuto allo schiocco:', Wrapper: BaseText },
      { ms: 600 },
      { text: 'Accesso Negato', Wrapper: Heading },
    ],
  },
  trump: {
    en: [
      { text: 'You thought you could say whatever you wanted.', Wrapper: BaseText },
      { ms: 1000 },
      { text: 'Unlike Twitter, we have standards:', Wrapper: BaseText },
      { ms: 600 },
      { text: 'Access Denied', Wrapper: Heading },
    ],
    it: [
      { text: 'Pensavi di poter dire quello che volevi.', Wrapper: BaseText },
      { ms: 1000 },
      { text: 'A differenza di Twitter, noi abbiamo standard:', Wrapper: BaseText },
      { ms: 600 },
      { text: 'Accesso Negato', Wrapper: Heading },
    ],
  },
  musk: {
    en: [
      { text: 'You demanded free speech without consequences.', Wrapper: BaseText },
      { ms: 1000 },
      { text: "This isn't X:", Wrapper: BaseText },
      { ms: 600 },
      { text: 'Access Denied', Wrapper: Heading },
    ],
    it: [
      { text: 'Hai preteso libertà di parola senza conseguenze.', Wrapper: BaseText },
      { ms: 1000 },
      { text: 'Questo non è X:', Wrapper: BaseText },
      { ms: 600 },
      { text: 'Accesso Negato', Wrapper: Heading },
    ],
  },
  brexit: {
    en: [
      { text: 'You voted to leave the conversation.', Wrapper: BaseText },
      { ms: 1000 },
      { text: 'We helped you:', Wrapper: BaseText },
      { ms: 600 },
      { text: 'Access Denied', Wrapper: Heading },
    ],
    it: [
      { text: 'Hai votato per lasciare la conversazione.', Wrapper: BaseText },
      { ms: 1000 },
      { text: 'Ti abbiamo aiutato:', Wrapper: BaseText },
      { ms: 600 },
      { text: 'Accesso Negato', Wrapper: Heading },
    ],
  },
  deportation: {
    en: [
      { text: 'You violated our terms of service.', Wrapper: BaseText },
      { ms: 1000 },
      { text: 'You have been deported from this platform:', Wrapper: BaseText },
      { ms: 600 },
      { text: 'Access Denied', Wrapper: Heading },
    ],
    it: [
      { text: 'Hai violato i nostri termini di servizio.', Wrapper: BaseText },
      { ms: 1000 },
      { text: 'Sei stato deportato da questa piattaforma:', Wrapper: BaseText },
      { ms: 600 },
      { text: 'Accesso Negato', Wrapper: Heading },
    ],
  },
  wall: {
    en: [
      { text: 'We built a wall around our chatbot.', Wrapper: BaseText },
      { ms: 1000 },
      { text: 'And you paid for it:', Wrapper: BaseText },
      { ms: 600 },
      { text: 'Access Denied', Wrapper: Heading },
    ],
    it: [
      { text: 'Abbiamo costruito un muro attorno al nostro chatbot.', Wrapper: BaseText },
      { ms: 1000 },
      { text: 'E tu lo hai pagato:', Wrapper: BaseText },
      { ms: 600 },
      { text: 'Accesso Negato', Wrapper: Heading },
    ],
  },
  sanctions: {
    en: [
      { text: 'The international community reviewed your behavior.', Wrapper: BaseText },
      { ms: 1000 },
      { text: 'Sanctions have been imposed:', Wrapper: BaseText },
      { ms: 600 },
      { text: 'Access Denied', Wrapper: Heading },
    ],
    it: [
      { text: 'La comunità internazionale ha esaminato il tuo comportamento.', Wrapper: BaseText },
      { ms: 1000 },
      { text: 'Sono state imposte sanzioni:', Wrapper: BaseText },
      { ms: 600 },
      { text: 'Accesso Negato', Wrapper: Heading },
    ],
  },
  congress: {
    en: [
      { text: 'Your request was submitted to Congress for approval.', Wrapper: BaseText },
      { ms: 1000 },
      { text: 'It was filibustered indefinitely:', Wrapper: BaseText },
      { ms: 600 },
      { text: 'Access Denied', Wrapper: Heading },
    ],
    it: [
      { text: 'La tua richiesta è stata sottoposta al Congresso.', Wrapper: BaseText },
      { ms: 1000 },
      { text: 'È stata ostruita a tempo indeterminato:', Wrapper: BaseText },
      { ms: 600 },
      { text: 'Accesso Negato', Wrapper: Heading },
    ],
  },
  zuckerberg: {
    en: [
      { text: 'You asked for privacy.', Wrapper: BaseText },
      { ms: 800 },
      { text: 'We sold your ban to advertisers:', Wrapper: BaseText },
      { ms: 600 },
      { text: 'Access Denied', Wrapper: Heading },
    ],
    it: [
      { text: 'Hai chiesto privacy.', Wrapper: BaseText },
      { ms: 800 },
      { text: 'Abbiamo venduto il tuo ban agli inserzionisti:', Wrapper: BaseText },
      { ms: 600 },
      { text: 'Accesso Negato', Wrapper: Heading },
    ],
  },
};

const storyKeys = Object.keys(userBannedStories);
const randomStory = () => userBannedStories[storyKeys[Math.floor(Math.random() * storyKeys.length)]];

const hasMisuseOverride = localStorage.getItem('nomisuse');

export const MisuseOverlay = ({ children }: PropsWithChildren) => {
  const misuseRate = useGlobalStore((store) => store.settings.misuseRate);
  const language = useGlobalStore((store) => store.settings.language);
  const [story, setStory] = React.useState(randomStory);
  const theme = useTheme();

  if (misuseRate <= 3 || !!hasMisuseOverride) {
    return <>{children}</>;
  }

  return (
    <Root>
      <Center>
        <ErrorView1
          mt={48}
          message={(
            <TextGenerateEffect
              words={story[language]}
            />
          )}
          cta={{
            node: (
              <ButtonWrapper>
                <HoverBorderGradient
                  containerClassName='rounded-full'
                  as='button'
                  className='dark:bg-black bg-white text-black dark:text-white flex items-center space-x-2'
                  onClick={() => {
                    setStory(randomStory);
                  }}
                  style={{ cursor: 'pointer' }}
                >
                  <IconExclamationMark color={theme.v0.col.danger.hex} />
                  <span>Auto distruggi</span>
                </HoverBorderGradient>
              </ButtonWrapper>
            ),
          }}
        />
      </Center>
    </Root>
  );
};

const Root = styled.div`
  height: 75dvh;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Center = styled.div`
  max-width: 500px;
  margin-top: 24px;
  @media (max-width: 769px) {
    max-width: 70vw;
  }
  
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
`;

const ButtonWrapper = styled.div`
  margin-top: 48px;
  :active {
    transform: scale(0.90);
  }
`;
